import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { History as HistoryIcon, Search as SearchIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MainLayout from '../../../components/layout/MainLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

interface UserLog {
  id: string;
  userId: string;
  roleCode: string;
  action: string;
  moduleCode: string;
  description: string;
  entity?: string;
  entityId?: string;
  status: string;
  errorMessage?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    role?: {
      code: string;
    };
  };
}

interface ApiResponse {
  items: UserLog[];
  total: number;
  page: number;
  limit: number;
}

const UserLogs: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<UserLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  
  const [filters, setFilters] = useState({
    action: '',
    userId: '',
    startDate: '',
    endDate: '',
    moduleCode: '',
    status: '',
  });
  
  const [users, setUsers] = useState<any[]>([]);
  const [actionOptions, setActionOptions] = useState<string[]>([]);

  useEffect(() => {
    fetchUsers();
    fetchActions();
    fetchLogs(1);
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/system-settings/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await axios.get('/system-settings/logs/meta/actions');
      setActionOptions(response.data);
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  const fetchLogs = async (pageNum: number) => {
    setLoading(true);
    try {
      const response = await axios.get<ApiResponse>('/system-settings/logs/users', {
        params: {
          page: pageNum,
          limit: limit,
          ...(filters.action && { action: filters.action }),
          ...(filters.userId && { userId: filters.userId }),
          ...(filters.startDate && { startDate: filters.startDate }),
          ...(filters.endDate && { endDate: filters.endDate }),
          ...(filters.moduleCode && { moduleCode: filters.moduleCode }),
          ...(filters.status && { status: filters.status }),
        }
      });
      
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
      setPage(response.data.page || pageNum);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    fetchLogs(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    fetchLogs(value);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'LOGIN': return 'success';
      case 'LOGOUT': return 'default';
      case 'CREATE': return 'primary';
      case 'UPDATE': return 'info';
      case 'DELETE': return 'error';
      case 'VIEW': return 'default';
      case 'OPEN_MODULE': return 'secondary';
      case 'CLICK_BUTTON': return 'default';
      case 'SUBMIT_FORM': return 'primary';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
      return status === 'SUCCESS' ? 'success' : 'error';
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/logs')} sx={{ mr: 2 }}>
            Back
          </Button>
          <HistoryIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">User Activity Logs</Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                select
                label="Module"
                size="small"
                value={filters.moduleCode}
                onChange={(e) => handleFilterChange('moduleCode', e.target.value)}
              >
                <MenuItem value="">All Modules</MenuItem>
                <MenuItem value="EXAMINATION">Examination</MenuItem>
                <MenuItem value="FEES">Fees</MenuItem>
                <MenuItem value="STUDENTS">Students</MenuItem>
                <MenuItem value="ATTENDANCE">Attendance</MenuItem>
                <MenuItem value="REPORTS">Reports</MenuItem>
                <MenuItem value="SYSTEM_SETTINGS">System Settings</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                select
                label="Action"
                size="small"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <MenuItem value="">All Actions</MenuItem>
                {actionOptions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                select
                label="Status"
                size="small"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="SUCCESS">Success</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                select
                label="User"
                size="small"
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                Filter
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                            {format(new Date(log.createdAt), 'dd MMM yyyy')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {format(new Date(log.createdAt), 'HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {log.user?.firstName} {log.user?.lastName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {log.user?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                          <Chip label={log.roleCode || 'N/A'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                          <Chip label={log.moduleCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={log.action} 
                          color={getActionColor(log.action) as any} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell>
                          <Chip 
                              label={log.status} 
                              color={getStatusColor(log.status) as any}
                              size="small"
                          />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap title={log.description}>
                            {log.description}
                        </Typography>
                        {log.errorMessage && (
                            <Typography variant="caption" color="error" display="block" noWrap title={log.errorMessage}>
                                Error: {log.errorMessage}
                            </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                          <Chip 
                              label={log.ipAddress || 'N/A'} 
                              size="small" 
                              variant="outlined"
                          />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination 
                count={Math.ceil(total / limit)} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
                showFirstButton 
                showLastButton
            />
          </Box>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default UserLogs;
